import { useState, ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, Copy, Mail, Send } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Role = {
  id: number
  name: string
  description: string
  is_system: boolean
}

type Invite = {
  id: number
  email: string
  role: number
  role_name: string
  invited_by: number | null
  invited_by_username: string | null
  token: string
  expires_at: string
  accepted_at: string | null
  accepted_by_username: string | null
  message: string
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED'
  created_at: string
  updated_at: string
}

const STATUS_COLOR: Record<Invite['status'], string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  EXPIRED: 'bg-gray-100 text-gray-600',
}

type CreateInput = { email: string; role: number | null; message: string }

function emptyDraft(): CreateInput {
  return { email: '', role: null, message: '' }
}

function inviteUrl(token: string): string {
  return `${window.location.origin}/invite/accept/${token}`
}

export default function AdminInvites() {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()

  const invitesQ = useQuery({
    queryKey: ['core', 'invites'],
    queryFn: async () => (await api.get<Invite[]>('/invites/')).data,
  })
  const rolesQ = useQuery({
    queryKey: ['core', 'roles'],
    queryFn: async () => (await api.get<Role[]>('/roles/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())

  const createMut = useMutation({
    mutationFn: async () => {
      if (!draft.email) throw new Error('Email is required')
      if (!draft.role) throw new Error('Role template is required')
      return (await api.post<Invite>('/invites/', {
        email: draft.email,
        role: draft.role,
        message: draft.message,
      })).data
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['core', 'invites'] })
      // Copy the link to clipboard immediately so the admin can paste it
      // into chat / email without an extra click.
      const url = inviteUrl(created.token)
      navigator.clipboard?.writeText(url).catch(() => { /* ignore */ })
      toast.success('Invite created — link copied to clipboard')
      setCreating(false); setDraft(emptyDraft())
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/invites/${id}/`),
    onSuccess: () => {
      toast.success('Invite revoked')
      qc.invalidateQueries({ queryKey: ['core', 'invites'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Resend the email for a pending invite. Surfaces whatever SMTP error
  // the backend reports — most common cause is bad creds in Email Settings.
  const resendMut = useMutation({
    mutationFn: async (id: number) =>
      (await api.post<{ ok: boolean; error: string; sent_to: string }>(
        `/invites/${id}/resend/`,
      )).data,
    onSuccess: (res) => {
      if (res.ok) toast.success(`Email re-sent to ${res.sent_to}`)
      else toast.error(`Send failed: ${res.error}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Read EmailSettings once so we can show "email configured" status next
  // to the header and warn before invite-create that emails won't go out.
  const emailQ = useQuery({
    queryKey: ['core', 'email-settings'],
    queryFn: async () =>
      (await api.get<{ is_configured: boolean; from_address: string }>(
        '/email-settings/',
      )).data,
    retry: false,  // some non-admin users won't have access; degrade quietly
  })

  const invites = invitesQ.data || []
  const roles = (rolesQ.data || []).filter((r) => r.is_system || true)
  const pending = invites.filter((i) => i.status === 'PENDING').length

  function copyLink(token: string) {
    const url = inviteUrl(token)
    navigator.clipboard?.writeText(url)
      .then(() => toast.success('Invite link copied'))
      .catch(() => toast.error('Couldn\'t copy — select the URL manually'))
  }

  function openMailto(invite: Invite) {
    const url = inviteUrl(invite.token)
    const subject = encodeURIComponent('You\'re invited to MooderERP')
    const body = encodeURIComponent(
      `Hi,\n\nYou've been invited to MooderERP as ${invite.role_name}. Use the link below to set your password and get started — it's good for 7 days:\n\n${url}\n\n${invite.message ? invite.message + '\n\n' : ''}—`
    )
    window.location.href = `mailto:${invite.email}?subject=${subject}&body=${body}`
  }

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Invitations</h2>
          <p className="text-sm text-muted-foreground">
            {invites.length} total · {pending} pending · invite-only signup for new users.
          </p>
        </div>
        <Button onClick={() => { setDraft(emptyDraft()); setCreating(true) }}>
          <Plus className="h-3 w-3 mr-1" /> New invite
        </Button>
      </div>

      {/* Email-configured banner. Without active email, the system can't
          auto-send invitations — admin has to share the link manually. */}
      {emailQ.data && !emailQ.data.is_configured && (
        <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          <strong>Email auto-send is off.</strong> Invites get a link you can copy and share manually.
          To enable automatic sending, finish configuring <a href="/admin/email-settings" className="underline">Email Settings</a>.
        </div>
      )}

      {invitesQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : invites.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No invites yet. Create one to onboard your first user.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Invited by</Th>
                <Th>Created</Th>
                <Th>Expires</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invites.map((inv) => (
                <tr key={inv.id} className="hover:bg-muted/40">
                  <Td>
                    <div className="font-medium text-foreground">{inv.email}</div>
                    {inv.message && (
                      <div className="text-xs text-muted-foreground truncate max-w-[24rem]" title={inv.message}>
                        “{inv.message}”
                      </div>
                    )}
                  </Td>
                  <Td className="text-sm">{inv.role_name}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[inv.status]}`}>
                      {inv.status}
                    </span>
                    {inv.status === 'ACCEPTED' && inv.accepted_at && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        by {inv.accepted_by_username} · {new Date(inv.accepted_at).toLocaleDateString()}
                      </div>
                    )}
                  </Td>
                  <Td className="text-xs">{inv.invited_by_username || '—'}</Td>
                  <Td className="text-xs">{new Date(inv.created_at).toLocaleDateString()}</Td>
                  <Td className="text-xs">
                    {inv.status === 'PENDING'
                      ? new Date(inv.expires_at).toLocaleDateString()
                      : <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-right">
                    {inv.status === 'PENDING' && (
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => copyLink(inv.token)}
                          className="p-1 rounded hover:bg-muted text-muted-foreground"
                          title="Copy invite link"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openMailto(inv)}
                          className="p-1 rounded hover:bg-muted text-muted-foreground"
                          title="Open in mail client"
                        >
                          <Mail className="h-3.5 w-3.5" />
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={!emailQ.data?.is_configured || resendMut.isPending}
                              onClick={() => resendMut.mutate(inv.id)}
                              title={
                                emailQ.data?.is_configured
                                  ? 'Re-send the invite email'
                                  : 'Configure Email Settings to enable auto-send'
                              }
                            >
                              <Send className="h-3.5 w-3.5 mr-2" />
                              Resend email
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => confirm(
                                `invite for ${inv.email}`,
                                () => deleteMut.mutate(inv.id),
                              )}
                              className="text-destructive"
                            >
                              Revoke invite
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New invitation"
        description="Creates an invite link. After saving, the link is copied to your clipboard so you can paste it into chat or email. Email auto-send arrives once transactional email is wired up."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create invite"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="inv-email">Email *</FieldLabel>
            <Input
              id="inv-email" type="email" required autoFocus
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value.trim() })}
              placeholder="alex@example.com"
            />
            <FieldDescription>
              Becomes their login + the first email on their Person record. Case-insensitive.
            </FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="inv-role">Role template *</FieldLabel>
            <SearchableSelect
              id="inv-role"
              value={draft.role ? String(draft.role) : NONE}
              onChange={(v) => setDraft({ ...draft, role: v === NONE ? null : Number(v) })}
              placeholder="— pick a role —"
              options={[
                { value: NONE, label: '— pick a role —' },
                ...roles.map((r) => ({
                  value: String(r.id),
                  label: r.name,
                  hint: r.is_system ? 'system' : undefined,
                })),
              ]}
            />
            <FieldDescription>
              Bundle of permissions auto-attached on accept. Adjust per-user later under Admin → Users.
            </FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="inv-msg">Personal note (optional)</FieldLabel>
            <Textarea
              id="inv-msg" rows={3} value={draft.message}
              onChange={(e) => setDraft({ ...draft, message: e.target.value })}
              placeholder="“Welcome to the team — set up your account here.”"
            />
            <FieldDescription>
              Shown to the recipient on the accept page. Keep it short.
            </FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
